import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getFirestore, doc, getDoc, updateDoc, collection, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { useUser } from '../components/LoginPersistence';
import { OrganizationCard } from './OrganizationCard';
import { CommunityMemberUpgradeForm } from './CommunityMemberUpgradeForm';
import Constants from 'expo-constants';
import { createOrganizationForUser, getMaxOrgsPerUser, getUserOrganizationCount } from '../utils/organizationUtils';


// Organizations Tab Component
export const OrganizationsTab = ({
  organizations,
  setOrganizations,
  organizationInvitations,
  setOrganizationInvitations,
  colors,
  profileStyles
}) => {
  const { user: userData } = useUser();
  const db = getFirestore();
  const [showCreateCard, setShowCreateCard] = useState(false);
  const [createdOrgId, setCreatedOrgId] = useState(null);
  const [createdOrgData, setCreatedOrgData] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showCommunityMemberSuccessModal, setShowCommunityMemberSuccessModal] = useState(false);

  // Create organization immediately on button click
  const handleCreateOrganization = async () => {
    if (!userData?.uid) {
      alert('You must be logged in to create an organization');
      return;
    }

    // Check if user is a community member
    if (!userData?.isCommunityMember) {
      setShowUpgradeModal(true);
      return;
    }

    // Check max organizations per user
    const maxOrgsPerUser = getMaxOrgsPerUser();
    if (maxOrgsPerUser > 0) {
      try {
        const currentOrgCount = await getUserOrganizationCount(db, userData.uid);
        
        if (currentOrgCount >= maxOrgsPerUser) {
          alert(`You have reached the maximum number of organizations (${maxOrgsPerUser}). Please leave an organization before creating a new one.`);
          return;
        }
      } catch (error) {
        console.error('Error checking organization limit:', error);
        alert('Failed to verify organization limit. Please try again.');
        return;
      }
    }

    setIsCreating(true);
    try {
      const orgData = await createOrganizationForUser(db, userData);

      setCreatedOrgId(orgData.id);
      setCreatedOrgData(orgData);
      setShowCreateCard(true);
      loadOrganizations();
    } catch (error) {
      console.error('Error creating organization:', error);
      alert('Failed to create organization. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  // Load organizations and invitations
  useEffect(() => {
    loadOrganizations();
    loadInvitations();
  }, [userData?.uid]);

  const loadOrganizations = async () => {
    if (!userData?.uid) return;
    try {
      // Load organizations where user is owner
      const ownedQuery = query(
        collection(db, 'organizations'),
        where('ownerId', '==', userData.uid)
      );
      const ownedSnapshot = await getDocs(ownedQuery);
      
      // Load organizations where user is a member
      const memberQuery = query(
        collection(db, 'organizations'),
        where('members', 'array-contains', userData.uid)
      );
      const memberSnapshot = await getDocs(memberQuery);

      const orgs = [];
      ownedSnapshot.forEach(doc => {
        orgs.push({ id: doc.id, ...doc.data(), role: 'owner' });
      });
      memberSnapshot.forEach(doc => {
        if (!orgs.find(o => o.id === doc.id)) {
          orgs.push({ id: doc.id, ...doc.data(), role: 'member' });
        }
      });
      
      setOrganizations(orgs);
    } catch (error) {
      console.error('Error loading organizations:', error);
    }
  };

  const loadInvitations = async () => {
    if (!userData?.uid) return;
    try {
      const invitationsQuery = query(
        collection(db, 'organization_invitations'),
        where('inviteeEmail', '==', userData.email),
        where('status', '==', 'pending')
      );
      const snapshot = await getDocs(invitationsQuery);
      const invites = [];
      snapshot.forEach(doc => {
        invites.push({ id: doc.id, ...doc.data() });
      });
      setOrganizationInvitations(invites);
    } catch (error) {
      console.error('Error loading invitations:', error);
    }
  };

  // Handle organization save callback
  const handleOrgSave = async () => {
    await loadOrganizations();
    // Reload the created org data
    if (createdOrgId) {
      const orgDoc = await getDoc(doc(db, 'organizations', createdOrgId));
      if (orgDoc.exists()) {
        setCreatedOrgData({ id: orgDoc.id, ...orgDoc.data(), role: 'owner' });
      }
    }
  };

  // Handle invite callback
  const handleInviteCallback = async () => {
    await loadInvitations();
  };

  // Handle close card
  const handleCloseCard = () => {
    setShowCreateCard(false);
    setCreatedOrgId(null);
    setCreatedOrgData(null);
  };


  const handleAcceptInvitation = async (invitationId, organizationId) => {
    try {
      const invitationRef = doc(db, 'organization_invitations', invitationId);
      const orgRef = doc(db, 'organizations', organizationId);
      
      const orgDoc = await getDoc(orgRef);
      if (!orgDoc.exists()) {
        alert('Organization not found');
        return;
      }

      const orgData = orgDoc.data();
      const currentMembers = orgData.members || [];
      
      // Check max members per organization
      const maxMembersPerOrg = parseInt(Constants.expoConfig?.extra?.maxMembersPerOrg || process.env.EXPO_PUBLIC_MAX_MEMBERS_PER_ORG || '0', 10);
      if (maxMembersPerOrg > 0 && currentMembers.length >= maxMembersPerOrg) {
        alert(`This organization has reached the maximum number of members (${maxMembersPerOrg}). You cannot join at this time.`);
        return;
      }

      // Check if user is already a member
      if (currentMembers.includes(userData.uid)) {
        alert('You are already a member of this organization.');
        // Update invitation status anyway
        await updateDoc(invitationRef, {
          status: 'accepted',
          acceptedAt: serverTimestamp()
        });
        loadInvitations();
        return;
      }

      // Check max organizations per user
      const maxOrgsPerUser = parseInt(Constants.expoConfig?.extra?.maxOrgsPerUser || process.env.EXPO_PUBLIC_MAX_ORGS_PER_USER || '0', 10);
      if (maxOrgsPerUser > 0) {
        // Count current organizations (owned + member)
        const ownedQuery = query(
          collection(db, 'organizations'),
          where('ownerId', '==', userData.uid)
        );
        const memberQuery = query(
          collection(db, 'organizations'),
          where('members', 'array-contains', userData.uid)
        );
        
        const [ownedSnapshot, memberSnapshot] = await Promise.all([
          getDocs(ownedQuery),
          getDocs(memberQuery)
        ]);

        // Get unique organization IDs (user might be both owner and member of same org)
        const orgIds = new Set();
        ownedSnapshot.forEach(doc => orgIds.add(doc.id));
        memberSnapshot.forEach(doc => orgIds.add(doc.id));

        const currentOrgCount = orgIds.size;
        
        if (currentOrgCount >= maxOrgsPerUser) {
          alert(`You have reached the maximum number of organizations (${maxOrgsPerUser}). Please leave an organization before joining another one.`);
          return;
        }
      }

      // All checks passed - add user to organization
      const updatedMembers = [...currentMembers, userData.uid];

      await updateDoc(orgRef, {
        members: updatedMembers,
        updatedAt: serverTimestamp()
      });

      await updateDoc(invitationRef, {
        status: 'accepted',
        acceptedAt: serverTimestamp()
      });

      loadOrganizations();
      loadInvitations();
      alert('Invitation accepted! You are now a member.');
    } catch (error) {
      console.error('Error accepting invitation:', error);
      alert('Failed to accept invitation. Please try again.');
    }
  };

  const handleRejectInvitation = async (invitationId) => {
    try {
      const invitationRef = doc(db, 'organization_invitations', invitationId);
      await updateDoc(invitationRef, {
        status: 'rejected',
        rejectedAt: serverTimestamp()
      });
      loadInvitations();
    } catch (error) {
      console.error('Error rejecting invitation:', error);
    }
  };


  return (
    <View>
      {/* Pending Invitations */}
      {organizationInvitations.length > 0 && (
        <View style={profileStyles.inputGroup}>
          <Text style={[profileStyles.inputLabel, { color: colors.purpleReplacement, marginBottom: 10 }]}>
            Pending Invitations
          </Text>
          {organizationInvitations.map(invitation => (
            <View key={invitation.id} style={{
              backgroundColor: '#f0f0f0',
              padding: 12,
              borderRadius: 8,
              marginBottom: 10,
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <View style={{ flex: 1 }}>
                <Text style={{ fontWeight: 'bold', marginBottom: 4 }}>
                  {invitation.organizationName}
                </Text>
                <Text style={{ fontSize: 12, color: '#666' }}>
                  Invited by {invitation.inviterEmail}
                </Text>
              </View>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <TouchableOpacity
                  style={{
                    backgroundColor: '#2ecc71',
                    padding: 8,
                    borderRadius: 5,
                    paddingHorizontal: 12
                  }}
                  onPress={() => handleAcceptInvitation(invitation.id, invitation.organizationId)}
                >
                  <Text style={{ color: '#fff', fontSize: 12 }}>Accept</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{
                    backgroundColor: '#e74c3c',
                    padding: 8,
                    borderRadius: 5,
                    paddingHorizontal: 12
                  }}
                  onPress={() => handleRejectInvitation(invitation.id)}
                >
                  <Text style={{ color: '#fff', fontSize: 12 }}>Reject</Text>
                </TouchableOpacity>
              </View>
            </View>
          ))}
        </View>
      )}

      {/* Create Organization Button - Only show if user is a community member */}
      {/* {!showCreateCard && userData?.isCommunityMember && ( */}
      <TouchableOpacity
        style={[profileStyles.signOutButton, { 
          backgroundColor: colors.purpleReplacement, 
          marginBottom: 20,
          padding: 20,
          minHeight: 60
        }]}
        onPress={handleCreateOrganization}
        disabled={isCreating}
      >
        {isCreating ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
            <ActivityIndicator size="small" color="#ffffff" style={{ marginRight: 10 }} />
            <Text style={[profileStyles.signOutText, { color: '#ffffff', fontSize: 18, fontWeight: 'bold' }]}>
              Creating...
            </Text>
          </View>
        ) : (
          <Text style={[profileStyles.signOutText, { color: '#ffffff', fontSize: 18, fontWeight: 'bold' }]}>
            Create Organization
          </Text>
        )}
      </TouchableOpacity>
      {/* )} */}

      {/* Create Organization Card - Organization is already created, no close button */}
      {showCreateCard && createdOrgId && createdOrgData && (
        <OrganizationCard
          orgId={createdOrgId}
          orgData={createdOrgData}
          colors={colors}
          profileStyles={profileStyles}
          onSave={handleOrgSave}
          onClose={null}
          onInvite={handleInviteCallback}
          isEditable={true}
        />
      )}

      {/* Organizations List */}
      {(() => {
        // Filter out the created organization from the list if create card is showing
        const filteredOrgs = showCreateCard && createdOrgId 
          ? organizations.filter(org => org.id !== createdOrgId)
          : organizations;
        
        if (filteredOrgs.length === 0 && !showCreateCard) {
          return (
            <Text style={{ textAlign: 'center', color: '#666', marginTop: 20 }}>
              You are not part of any organizations yet
            </Text>
          );
        }
        
        return filteredOrgs.map(org => (
          <OrganizationCard
            key={org.id}
            orgId={org.id}
            orgData={org}
            colors={colors}
            profileStyles={profileStyles}
            onSave={handleOrgSave}
            onClose={null}
            onInvite={handleInviteCallback}
            isEditable={true}
          />
        ));
      })()}

      {/* Community Member Upgrade Modal */}
      <Modal
        visible={showUpgradeModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowUpgradeModal(false)}
      >
        <View style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}>
          <CommunityMemberUpgradeForm
            isModal={true}
            onClose={() => setShowUpgradeModal(false)}
            message="You must be a community member to create an organization."
            onSuccess={() => {
              setShowUpgradeModal(false);
              setShowCommunityMemberSuccessModal(true);
            }}
            onError={(error) => {
              console.error('Error upgrading to community member:', error);
            }}
          />
        </View>
      </Modal>

      {/* Community Member Success Modal */}
      <Modal
        visible={showCommunityMemberSuccessModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowCommunityMemberSuccessModal(false)}
      >
        <View style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.5)',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20,
        }}>
          <View style={{
            backgroundColor: '#ffffff',
            borderRadius: 12,
            padding: 20,
            width: '100%',
            maxWidth: 400,
            alignItems: 'center',
          }}>
            <Ionicons name="checkmark-circle" size={48} color="#2ecc71" />
            <Text style={{
              fontSize: 18,
              fontFamily: 'Glacial Indifference',
              marginTop: 10,
              marginBottom: 15,
              textAlign: 'center',
              color: '#2ecc71',
            }}>Congratulations!</Text>

            <Text style={{
              fontSize: 14,
              color: '#333',
              textAlign: 'center',
              marginBottom: 20,
              lineHeight: 20,
            }}>You are now a community member.</Text>

            <TouchableOpacity
              onPress={() => setShowCommunityMemberSuccessModal(false)}
              style={{
                backgroundColor: '#2ecc71',
                borderRadius: 8,
                paddingHorizontal: 30,
                paddingVertical: 12,
                alignItems: 'center',
              }}
            >
              <Text style={{
                color: '#ffffff',
                fontSize: 16,
                fontFamily: 'Glacial Indifference',
                fontWeight: 'bold',
              }}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

